import {
  Box,
  Button,
  Col,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Textarea,
  Flex,
  Image,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { notifications } from 'notifications';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { useLoginUser } from 'redux/selectors/useLoginUser';
import { API } from 'services';
import { GradientButton } from 'components/GradientButton';
import loading from 'assets/report/loading.gif';

export function TimesheetGenerate() {
  const navigate = useNavigate();
  const { loginUser } = useLoginUser();
  const [isLoading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCheckingDate, setIsCheckingDate] = useState(false);
  const [isDateAllowed, setIsDateAllowed] = useState(true);

  const form = useForm({
    initialValues: {
      date: new Date(),
      sign_in_coa: '',
      course_name: '',
      course_run_id: '',
      remarks: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.course_name?.trim()) errors.course_name = 'Please select Course Name';
      if (!values.course_run_id?.trim()) errors.course_run_id = 'Please select Course Run';
      return errors;
    },
  });

  // Fetch available courses for dropdown
  const coursesQuery = useQuery(['courses'], () => {
    return API.getCourseByCoAssessor().then((res: any) => res.data);
  });

  // Fetch course runs when course is selected
  const courseRunsQuery = useQuery(
    ['courseRuns', form.values.course_name, selectedDate],
    async () => {
      if (!form.values.course_name) return [];
      // Calculate 2 months before start and selected month end
      const prevMonth = new Date(selectedDate);
      prevMonth.setMonth(prevMonth.getMonth() - 2);
      const startOfPrevMonth = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
      const endOfSelectedMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      );
      const dateStart = startOfPrevMonth.toISOString().split('T')[0];
      const dateEnd = endOfSelectedMonth.toISOString().split('T')[0];
      const runs = await API.getCourseRun({
        course_id: form.values.course_name,
        date_start: dateStart,
        date_end: dateEnd,
        from_type: 'Timesheet',
      }).then((res: any) => res.data);

      return runs;
    },
    {
      enabled: !!form.values.course_name,
    }
  );

  useEffect(() => {
    const value = form.values.course_run_id;
    if (!value) return;

    handleCourseRunChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.course_run_id]);

  // Clear course run when course changes
  useEffect(() => {
    if (form.values.course_name) {
      form.setFieldValue('course_run_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.course_name]);

  useEffect(() => {
    if (courseRunsQuery.data && courseRunsQuery.data.length > 0 && !form.values.course_run_id) {
      form.setFieldValue('course_run_id', courseRunsQuery.data[0].courserun_id?.toString() || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseRunsQuery.data]);

  // Auto select 'in' for new entries
  useEffect(() => {
    const now = new Date();
    const formatted = now.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    form.setFieldValue('sign_in_coa', formatted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const courseOptions =
    coursesQuery.data?.map((course: any) => ({
      value: course.value.toString(),
      label: course.label,
    })) || [];

  const courseRunOptions =
    courseRunsQuery.data?.map((courseRun: any) => {
      const fromDate = courseRun.courserun_course_from
        ? new Date(courseRun.courserun_course_from).toLocaleDateString('en-GB')
        : '';
      const toDate = courseRun.courserun_course_to
        ? new Date(courseRun.courserun_course_to).toLocaleDateString('en-GB')
        : '';
      const label =
        fromDate && toDate
          ? `${fromDate} - ${toDate}`
          : courseRun.courserun_reference_number || courseRun.courserun_id?.toString() || '';

      return {
        value: courseRun.courserun_id?.toString() || '',
        label: label,
      };
    }) || [];

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const timesheetData = {
        ...values,
        date: selectedDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        trainer_id: loginUser.trainer_id,
      };
      const response = await API.submitTimesheet(timesheetData);

      if (response.data.status) {
        notifications.success({
          title: 'Timesheet submitted successfully',
          message: '',
        });
        navigate('/timesheets');
      } else {
        notifications.error({
          title: 'Operation failed',
          message: response.data.message || 'Please try again',
        });
      }
    } catch (error) {
      notifications.error({
        title: 'Operation failed',
        message: 'Please try again',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseRunChange = async (courserunId: string) => {
    if (!courserunId) return;

    const payload = {
      trainer_id: loginUser.trainer_id ?? loginUser.id,
      courserun_id: courserunId,
    };

    try {
      const response = await API.validateTimesheetDate(payload);

      const isAllowed = response?.data?.status !== 0;
      setIsDateAllowed(isAllowed);

      if (!isAllowed) {
        notifications.error({
          title: 'Not allowed',
          message:
            response?.data?.message || 'This course run is not allowed for the selected date',
        });
      }
    } catch (error) {
      console.error(error);
      notifications.error({
        title: 'Error',
        message: 'Failed to validate course run',
      });
    } finally {
      setIsCheckingDate(false);
    }
  };

  return (
    <Stack px={{ base: 0, md: 30 }}>
      {(isLoading || coursesQuery.isLoading) && (
        <Box className={'loading_box'}>
          <Image src={loading} width="100"></Image>
        </Box>
      )}
      {!isLoading && coursesQuery.data && !coursesQuery.isError && (
        <Flex className={'flex_wrap'}>
          <Box className={'w100 md_w100'}>
            <Box bg="#FFF" p={20}>
              <Box
                mb={20}
                className="flex flex_direction_row flex_align_center flex_justify_between"
              >
                <Title order={2}>Timesheet</Title>
              </Box>
              <Box>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Grid>
                    <Col span={12} sm={6}>
                      <Text className="invoice_date_title">Date</Text>
                      <DatePicker
                        selected={selectedDate}
                        onChange={(date: any) => null}
                        dateFormat="dd/MM/yyyy"
                        wrapperClassName={`custom-datepicker-wrapper disabled`}
                        disabled={true}
                        readOnly={true}
                      />
                    </Col>
                    <Col span={12} sm={6}>
                      <TextInput
                        label="Sign In (COA)"
                        placeholder="Enter sign in COA"
                        readOnly
                        disabled
                        {...form.getInputProps('sign_in_coa')}
                      />
                    </Col>
                    <Col span={12} sm={6}>
                      <Select
                        label="Course Name"
                        placeholder="Select course"
                        data={courseOptions}
                        {...form.getInputProps('course_name')}
                      />
                    </Col>
                    <Col span={12} sm={6}>
                      <Select
                        label="Course Run"
                        placeholder="Select course run"
                        data={courseRunOptions}
                        disabled={!form.values.course_name || courseRunsQuery.isLoading}
                        {...form.getInputProps('course_run_id')}
                      />
                    </Col>
                    <Col span={12}>
                      <Textarea
                        label="Remarks"
                        placeholder="Enter any additional remarks"
                        {...form.getInputProps('remarks')}
                      />
                    </Col>
                  </Grid>
                  <hr
                    style={{ borderTop: '1px solid', borderColor: '#ddd', marginTop: '20px' }}
                  ></hr>
                  <Group position="right" mt="xl">
                    <Button variant="outline" onClick={() => navigate('/timesheets')}>
                      Cancel
                    </Button>
                    <GradientButton type="submit" loading={isLoading} disabled={!isDateAllowed}>
                      Submit Timesheet
                    </GradientButton>
                  </Group>
                </form>
              </Box>
            </Box>
          </Box>
        </Flex>
      )}
      {!isLoading && coursesQuery.isError && (
        <Box className={'error_box'}>
          <Text color="red">Failed to load course data</Text>
        </Box>
      )}
    </Stack>
  );
}
